import type { UploadAuthorisation } from '../../services/streamUploadService';

/**
 * Remembering an upload authorisation per file, so an interrupted upload resumes into
 * the same Bunny video instead of starting an empty second one.
 *
 * Kept separate from the component so the rules can be tested directly: these are the
 * decisions that fail silently. Get one wrong and the symptom is not an error - it is a
 * library slowly filling with half-uploaded duplicates, or a resume sent to a video
 * whose authorisation has already lapsed.
 */

const STORE_PREFIX = 'strymhub.streamUpload.';

/** A minute of margin: an authorisation expiring mid-request looks like a network fault. */
export const EXPIRY_MARGIN_MS = 60_000;

/**
 * Identity of a file for resuming: name, size AND last-modified.
 *
 * Last-modified matters. Re-exporting a film from DaVinci under the same name produces a
 * different file, often of similar size. Treating it as "the same file" would resume the
 * NEW export onto the OLD export's partial upload - silently splicing two different
 * cuts into one video.
 */
export function fileKey(file: Pick<File, 'name' | 'size' | 'lastModified'>): string {
  return `${STORE_PREFIX}${file.name}:${file.size}:${file.lastModified}`;
}

export function rememberedAuthorisation(
  file: Pick<File, 'name' | 'size' | 'lastModified'>,
  now: number = Date.now(),
): UploadAuthorisation | null {
  try {
    const raw = localStorage.getItem(fileKey(file));
    if (!raw) return null;
    const auth = JSON.parse(raw) as UploadAuthorisation;
    if (!auth?.videoId || typeof auth.expiresAt !== 'number') {
      localStorage.removeItem(fileKey(file));
      return null;
    }
    if (auth.expiresAt * 1000 < now + EXPIRY_MARGIN_MS) {
      localStorage.removeItem(fileKey(file));
      return null;
    }
    return auth;
  } catch {
    return null;
  }
}

export function remember(file: Pick<File, 'name' | 'size' | 'lastModified'>, auth: UploadAuthorisation) {
  try {
    localStorage.setItem(fileKey(file), JSON.stringify(auth));
  } catch {
    // Storage unavailable (private browsing). Uploading still works; only resuming
    // after a closed tab does not.
  }
}

export function forget(file: Pick<File, 'name' | 'size' | 'lastModified'>) {
  try {
    localStorage.removeItem(fileKey(file));
  } catch {
    /* nothing to clean up */
  }
}
